import React, { useEffect } from 'react';
import { Badge } from '@mui/material';
import { Link, useLocation } from 'react-router-dom';
import NN_logo from 'assets/icons/white/NN_logo.svg';
import DH_logo from 'assets/icons/blue/DH_logo.svg';
import { Auth } from 'aws-amplify';
import useCountNotifications from './hooks/useCountNotifications';
import clsx from 'clsx';
import { NDivider, NIcon, NTooltip } from '@innersource-nn/novoui-react';
import EmptyFolderIcon from 'assets/icons/EmptyFolderIcon';
import OrganizationIcon from 'assets/icons/OrganizationIcon';
import PipelineIcon from 'assets/icons/PipelineIcon';
import WarehouseIcon from 'assets/icons/WarehouseIcon';
import FeedbackIcon from 'assets/icons/FeedbackIcon';

interface DrawerItem {
    name: string;
    icon: React.JSX.Element;
    link: string;
}
interface ButtonConfigProps {
    item: DrawerItem;
    className: string;
    index: number;
    id: number;
    children?: React.ReactNode;
}

interface SidebarProps {
    email: string;
    closeSidebar: boolean;
}

interface ClassNameProps {
    className?: string;
}
type DrawerItemsGroup = DrawerItem[];

type SidebarData = DrawerItemsGroup[];
const props = { className: 'fill-white' };
const sidebarData: SidebarData = [
    [
        {
            name: 'Home',
            icon: <NIcon iconName="home" size={16} {...props} />,
            link: '/discover',
        },
        {
            name: 'Notifications',
            icon: <NIcon iconName="notifications" size={16} {...props} />,
            link: '/notifications',
        },
    ],
    [
        {
            name: 'My Datasets',
            icon: <EmptyFolderIcon {...props} />,
            link: '/datasets',
        },

        {
            name: 'Environments',
            icon: <NIcon iconName="cloud" size={16} {...props} />,
            link: '/environments',
        },
        {
            name: 'Organizations',
            icon: <OrganizationIcon {...props} />,
            link: '/organizations',
        },
    ],
    [
        {
            name: 'Automate Pipeline',
            icon: <PipelineIcon {...props} />,
            link: '/pipelines',
        },
        {
            name: 'Warehouses',
            icon: <WarehouseIcon {...props} />,
            link: '/warehouses',
        },
    ],
];

const documentationItems: DrawerItem[] = [
    {
        name: 'User Guides',
        icon: <NIcon iconName="guide" size={16} {...props} />,
        link: process.env.REACT_APP_DATAHUB_DOCS_LINK || '',
    },
    {
        name: 'Feedback',
        icon: <FeedbackIcon {...props} />,
        link: process.env.REACT_APP_TEAMS_LINK || '',
    },
];

const Sidebar: React.FC<SidebarProps> = ({ email, closeSidebar }) => {
    const { notifications, fetchNotifications } = useCountNotifications();
    const location = useLocation();
    const relativePath = location.pathname;

    useEffect(() => {
        const interval = setInterval(() => {
            fetchNotifications();
        }, 40000); // fetch notifications every 40 seconds

        return () => clearInterval(interval);
    }, [fetchNotifications]);

    const isSelected = (path: string) => {
        if (path === '/discover') {
            return relativePath.includes(path) || relativePath === '/';
        } else {
            return relativePath.includes(path.slice(0, -1));
        }
    };

    const signOut = async () => {
        try {
            await Auth.signOut();
        } catch (error) {
            console.log('error signing out: ', error);
        }
    };
    const UserInfo = () => {
        return (
            <div className="sidebar-user-info">
                <img className="w-40" src={DH_logo} alt="dh-logo" />
                {!closeSidebar && (
                    <div className="flex items-center justify-between w-full">
                        <div className="inline-block text-white">
                            <b>DataHub </b>
                            <p>{email}</p>
                        </div>
                        <LogoutButton />
                    </div>
                )}
            </div>
        );
    };

    const ButtonConfig: React.FC<ButtonConfigProps> = ({
        item,
        className,
        index,
        id,
        children,
    }) => {
        return (
            <div
                className={clsx(
                    isSelected(item.link) && 'bg-sea-blue-600',
                    className,
                    'sidebar-item',
                )}
                key={`sidebar-div-link-item-${index}`}
            >
                {item.icon}

                {children}
            </div>
        );
    };
    const ButtonItems: React.FC<ClassNameProps> = ({ className }) => {
        return sidebarData.map((container, id) => {
            return (
                <div key={`sidebar-container-${id}`}>
                    {container.map((item, index) => {
                        return closeSidebar ? (
                            <a
                                href={item.link}
                                key={`sidebar-2-link-item-${index}`}
                            >
                                <NTooltip
                                    content={
                                        item.name === 'Notifications'
                                            ? `${notifications} notifications`
                                            : item.name
                                    }
                                    key={`${item.name}-${index}-tooltip`}
                                    placement="right"
                                    variant="light"
                                >
                                    <ButtonConfig
                                        item={item}
                                        className={
                                            className +
                                            ' justify-center w-40 h-40'
                                        }
                                        index={index}
                                        id={id}
                                    >
                                        {item.name === 'Notifications' &&
                                            notifications > 0 && (
                                                <Badge
                                                    className="mb-12"
                                                    badgeContent=""
                                                    variant="dot"
                                                    color="error"
                                                    overlap="circular"
                                                />
                                            )}
                                    </ButtonConfig>
                                </NTooltip>
                            </a>
                        ) : (
                            <Link
                                to={item.link}
                                key={`sidebar-link-item-${index}`}
                            >
                                <ButtonConfig
                                    item={item}
                                    className={
                                        className + ' justify-start p-12'
                                    }
                                    index={index}
                                    id={id}
                                >
                                    <p className="self-center flex gap-16 text-white">
                                        {item.name}
                                    </p>
                                    {item.name === 'Notifications' &&
                                        notifications > 0 && (
                                            <Badge
                                                badgeContent={notifications}
                                                color="error"
                                                className="self-center"
                                            />
                                        )}
                                </ButtonConfig>
                            </Link>
                        );
                    })}
                    <NDivider
                        className="my-16"
                        key={`sidebar-divider-${id}`}
                        orientation="horizontal"
                    />
                </div>
            );
        });
    };

    const LinkItems: React.FC<ClassNameProps> = ({ className }) => {
        return documentationItems.map((item, index) => (
            <a
                href={item.link}
                target="_blank"
                rel="noopener noreferrer"
                className={clsx(
                    closeSidebar && 'sidebar-link',
                    `justify-${className} `,
                )}
                key={`sidebar-2-link-item-${index}`}
            >
                {!closeSidebar ? (
                    <div className={`justify-${className} sidebar-link`}>
                        {item.icon}
                        <p className="self-center block">{item.name}</p>
                    </div>
                ) : (
                    <NTooltip
                        content={item.name}
                        placement="right"
                        variant="light"
                    >
                        {item.icon}
                    </NTooltip>
                )}
            </a>
        ));
    };

    const LogoItem: React.FC<ClassNameProps> = ({ className }) => {
        return (
            <div className="flex justify-center">
                <img
                    className={`${className} sidebar-logo `}
                    src={NN_logo}
                    alt="NN-logo"
                />
            </div>
        );
    };

    const LogoutButton = () => {
        return (
            <div
                className={clsx(
                    closeSidebar && 'justify-center  sidebar-link',
                    'cursor-pointer',
                )}
            >
                <NTooltip placement="right" content="Log out" variant="light">
                    <NIcon
                        className="cursor-pointer fill-white"
                        iconName="signOut"
                        size="small"
                        type="button"
                        onClick={signOut}
                    />
                </NTooltip>
            </div>
        );
    };
    return closeSidebar ? (
        /* Sidebar is closed */
        <div className="w-[5rem] sidebar-div px-20">
            <UserInfo />
            <ButtonItems className="my-4" />
            <LinkItems className="center" />
            <LogoutButton />
            <LogoItem className="w-14 " />
        </div>
    ) : (
        /* Sidebar is open */
        <div className="w-80 sidebar-div px-24">
            <UserInfo />
            <ButtonItems className="justify-start px-16 gap-16 my-8 " />
            <LinkItems className="start" />
            <LogoItem />
        </div>
    );
};

export default Sidebar;
